import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { fillMenuTypeSlots, onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #forcedSticky = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Prepare the panel when its summary receives input.
    this.addEventListener('pointerdown', this.#prime);
    this.addEventListener('focusin', this.#prime);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('pointerdown', this.#prime);
    this.removeEventListener('focusin', this.#prime);
  }

  /**
   * Hydrate the panel belonging to the summary being pressed or focused, before it is opened.
   *
   * `hydrateDrawerPanels` is synchronous and idempotent — it removes each template as it clones
   * it — so `open()` re-running it later is a no-op and a double-tap cannot clone twice.
   *
   * @param {Event} event
   */
  #prime = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('summary')) return;

    const details = this.#getDetailsElement(event);

    if (!hydrateDrawerPanels(details)) return;

    this.#setupAnimatedElementListeners(details);

    // Lazy images inside a still-closed panel never begin loading, so the banners would pop in one
    // by one after the panel is revealed. Drop `loading="lazy"` on the panel the shopper is about
    // to open so those fetches start during the press. Scoped to the panel just cloned — panels
    // one level deeper are still in their own `<template>` and stay untouched. Mirrors
    // `#preloadImages` in `assets/header-menu.js`.
    for (const image of details.querySelectorAll('img[loading="lazy"]')) {
      image.removeAttribute('loading');
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    const details = this.#getDetailsElement(event);

    if (details === this.refs.details && event?.target instanceof HTMLElement && event.target.closest('summary')) {
      event.preventDefault();
    }
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);

    // Fill the deferred submenu panel *before* anything opens, synchronously, inside the very
    // event handler that triggered the open. No idle/load/hydration gating — a panel can never
    // be revealed while its content is still sitting in a <template>.
    if (hydrateDrawerPanels(details)) {
      this.#setupAnimatedElementListeners(details);
    }

    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      details.setAttribute('open', '');

      const header = /** @type {HTMLElement | null} */ (this.closest('header-component'));
      if (header) {
        header.setAttribute('data-prevent-scroll-sticky', 'true');
        if (header.dataset.stickyState !== 'active') {
          header.dataset.stickyState = 'active';
          this.#forcedSticky = true;
        }
      }
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();

          const header = /** @type {HTMLElement | null} */ (this.closest('header-component'));
          if (header) {
            if (!document.body.classList.contains('open-search')) {
              header.removeAttribute('data-prevent-scroll-sticky');
            }
            if (this.#forcedSticky) {
              header.dataset.stickyState = 'inactive';
              this.#forcedSticky = false;
            }
          }

          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   *
   * @param {Element} [root] - Limits the search to one subtree.
   */
  #setupAnimatedElementListeners(root = this) {
    const allAnimated = root.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Adds the direct deferred panels and their matching menu blocks.
 * Nested panels stay deferred until their details element opens.
 * @param {Element | null} details - The details element being opened.
 * @returns {boolean} Whether the function added content.
 */
function hydrateDrawerPanels(details) {
  if (!details) return false;

  let hydrated = false;

  for (const template of details.querySelectorAll('template[data-drawer-panel]')) {
    if (!(template instanceof HTMLTemplateElement)) continue;
    if (template.closest('details') !== details) continue;

    const parent = template.parentElement;
    if (!parent) continue;

    parent.insertBefore(template.content.cloneNode(true), template);
    template.remove();
    hydrated = true;
  }

  const filled = fillMenuTypeSlots(details, details.closest('header-drawer'));

  return hydrated || filled;
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
